import { apiUrl } from "../api/endpoints";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { GridActionsCellItem } from "@mui/x-data-grid";
import Tooltip from "@mui/material/Tooltip";
import AlarmOnOutlinedIcon from "@mui/icons-material/AlarmOnOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";

import { useAuth } from "../hooks/AuthProvider";
import DataGridTable from "../Components/DataGridTable";
import Spinner from "../Components/Spinner";
import Alert from "../Components/Alert";
import { getErrorMessage } from "../utils/errorUtils";
import { LanguageContext } from "../context/LanguageContext";
import plansExtraTranslations from "../languages/plansExtra.json";
import "./AdminTablePages.css";

export const DevicePlanLifecycle = () => {
  const { lang } = useContext(LanguageContext);
  const { user } = useAuth();
  const t = plansExtraTranslations[lang]?.devicePlanLifecycle || plansExtraTranslations.en.devicePlanLifecycle;
  const isSuperUser = String(user?.role || "").toLowerCase() === "super";
  const [filter, setFilter] = useState("all");
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertState, setAlertState] = useState({
    open: false,
    message: "",
    type: "info",
    position: ["ar", "ur"].includes(lang) ? "left" : "right",
  });

  const showAlert = useCallback((type, message) => {
    setAlertState({
      open: true,
      type,
      message,
      position: ["ar", "ur"].includes(lang) ? "left" : "right",
    });
  }, [lang]);

  const askDurationDays = useCallback(async (defaultValue = "") => {
    const input = window.prompt(t.promptEnterDuration, String(defaultValue || ""));
    if (input === null) return null;
    const n = Number(input);
    if (!Number.isInteger(n) || n <= 0) {
      showAlert("error", t.alerts.durationPositiveInteger);
      return undefined;
    }
    return n;
  }, [showAlert, t.alerts.durationPositiveInteger, t.promptEnterDuration]);

  const filterOptions = useMemo(
    () => [
      { value: "all", label: t.filterOptions.all },
      { value: "pending", label: t.filterOptions.pending },
      { value: "about_to_expire", label: t.filterOptions.aboutToExpire },
      { value: "expired", label: t.filterOptions.expired },
    ],
    [t]
  );

  const getPlanStatusLabel = useCallback(
    (status) => {
      const value = String(status || "").trim().toLowerCase();
      if (value === "pending_activation") return t.statuses.pendingActivation;
      if (value === "expired") return t.statuses.expired;
      if (value === "disabled") return t.statuses.disabled;
      if (value === "expiring_soon" || value === "about_to_expire") return t.statuses.expiringSoon;
      return t.statuses.active;
    },
    [t]
  );

  const fetchRows = useCallback(async () => {
    if (!isSuperUser) return;
    setLoading(true);
    try {
      const sort = sortModel?.[0] || {};
      const res = await axios.get(apiUrl(`device/plan-expiry`), {
        params: {
          filter,
          page,
          pageSize,
          sortField: sort.field || "expired_at",
          sortOrder: sort.sort || "asc",
          filterModel: JSON.stringify(filterModel || { items: [] }),
        },
      });
      const nextRows = Array.isArray(res?.data?.data) ? res.data.data : [];
      setRows(nextRows);
      setRowCount(Number(res?.data?.pagination?.total ?? nextRows.length));
    } catch (err) {
      showAlert("error", getErrorMessage(err));
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [filter, filterModel, isSuperUser, page, pageSize, showAlert, sortModel]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const applyPlanAction = useCallback(
    async (row, action) => {
      try {
        const payload = { action };
        if (["activate", "renew", "change_plan"].includes(action)) {
          const days = await askDurationDays(row?.duration_plan || "");
          if (days === null || days === undefined) return;
          payload.duration_plan = days;
        }

        await axios.post(apiUrl(`device/plan-action/${row.id}`), payload, {
          headers: { "Content-Type": "application/json" },
        });
        showAlert("info", t.alerts.actionApplied);
        fetchRows();
      } catch (err) {
        showAlert("error", getErrorMessage(err));
      }
    },
    [askDurationDays, fetchRows, showAlert, t.alerts.actionApplied]
  );

  const columns = useMemo(
    () => [
      {
        field: "actions",
        headerName: t.columns.actions,
        type: "actions",
        width: 100,
        getActions: (params) =>
          [
            params.row.plan_status === "pending_activation" ? (
              <GridActionsCellItem
                icon={
                  <Tooltip title={t.actions.activateRequest}>
                    <AlarmOnOutlinedIcon />
                  </Tooltip>
                }
                showInMenu
                label={t.actions.activateRequest}
                onClick={() => applyPlanAction(params.row, "activate")}
              />
            ) : null,
            params.row.plan_status === "expired" ? (
              <GridActionsCellItem
                icon={
                  <Tooltip title={t.actions.renewPlan}>
                    <AutorenewOutlinedIcon />
                  </Tooltip>
                }
                showInMenu
                label={t.actions.renewPlan}
                onClick={() => applyPlanAction(params.row, "renew")}
              />
            ) : null,
            params.row.plan_status === "active" ? (
              <GridActionsCellItem
                icon={
                  <Tooltip title={t.actions.changePlan}>
                    <AutorenewOutlinedIcon />
                  </Tooltip>
                }
                showInMenu
                label={t.actions.changePlan}
                onClick={() => applyPlanAction(params.row, "change_plan")}
              />
            ) : null,
            params.row.plan_status !== "disabled" ? (
              <GridActionsCellItem
                icon={
                  <Tooltip title={t.actions.disableDevicePlan}>
                    <BlockOutlinedIcon />
                  </Tooltip>
                }
                showInMenu
                label={t.actions.disableDevicePlan}
                onClick={() => applyPlanAction(params.row, "disable")}
              />
            ) : null,
          ].filter(Boolean),
      },
      { field: "id", headerName: t.columns.deviceId, width: 120, align: "center", headerAlign: "center" },
      { field: "subid", headerName: t.columns.subId, width: 100, align: "center", headerAlign: "center" },
      { field: "name", headerName: t.columns.deviceName, width: 220, align: "left", headerAlign: "left" },
      { field: "did", headerName: t.columns.did, width: 160, align: "center", headerAlign: "center" },
      { field: "owner_name", headerName: t.columns.owner, width: 180, align: "left", headerAlign: "left" },
      {
        field: "plan_status",
        headerName: t.columns.planStatus,
        width: 170,
        align: "center",
        headerAlign: "center",
        valueFormatter: (params) => getPlanStatusLabel(params),
      },
      {
        field: "duration_plan",
        headerName: t.columns.durationDays,
        width: 170,
        type: "number",
        align: "center",
        headerAlign: "center",
      },
      {
        field: "expired_at",
        headerName: t.columns.expiredAt,
        width: 200,
        type: "dateTime",
        valueGetter: (params) => (params ? new Date(params) : null),
        align: "center",
        headerAlign: "center",
      },
      {
        field: "days_to_expire",
        headerName: t.columns.daysToExpire,
        width: 170,
        type: "number",
        align: "center",
        headerAlign: "center",
      },
      {
        field: "plan_request_note",
        headerName: t.columns.requestNote,
        width: 260,
        align: "left",
        headerAlign: "left",
      },
    ],
    [applyPlanAction, getPlanStatusLabel, t]
  );

  if (!isSuperUser) {
    return (
      <div className="configuration devices-page admin-table-page">
          <div className="device-main">
          <div className="admin-table-empty">{t.noPermission}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="configuration devices-page admin-table-page">
      <Alert
        open={alertState.open}
        close={() => setAlertState((s) => ({ ...s, open: false }))}
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />

      <div className="device-main">
        <div className="devices-header">
          <div className="devices-header-left">
            <h2 className="devices-title">{t.title}</h2>
          </div>
          <div className="admin-table-header-actions">
            <button
              type="button"
              className="devices-secondary-btn admin-table-action-btn"
              onClick={fetchRows}
              disabled={loading}
            >
              <RefreshIcon fontSize="small" />
              <span>{t.refresh}</span>
            </button>
          </div>
        </div>

        {loading && rows.length === 0 && rowCount === 0 && !(filterModel?.items || []).length ? (
          <Spinner />
        ) : rows.length > 0 || rowCount > 0 || (filterModel?.items || []).length > 0 ? (
          <div className="admin-table-grid">
            <DataGridTable
              rows={rows}
              columns={columns}
              idcolumn="id"
              pagingoptions={[10, 20, 50, 100]}
              sortField="id"
              sortType="desc"
              showToolbar={true}
              tableKey="device_plan_lifecycle"
              loading={loading}
              pagination={{
                serverSide: true,
                page,
                pageSize,
                count: rowCount,
                sortModel,
                onPageChange: setPage,
                onPageSizeChange: (newSize) => {
                  setPageSize(newSize);
                  setPage(1);
                },
                onSortModelChange: (newModel) => {
                  setSortModel(newModel && newModel.length ? newModel : []);
                  setPage(1);
                },
                onFilterModelChange: (newModel) => {
                  setFilterModel(newModel || { items: [] });
                  setPage(1);
                },
              }}
              toolbarPrefix={
                <div className="admin-table-toolbar-left">
                  <div className="admin-table-filter-wrap">
                    <span className="admin-table-filter-label">{t.filter}</span>
                    <select
                      value={filter}
                      onChange={(e) => {
                        setFilter(e.target.value);
                        setPage(1);
                      }}
                      className="admin-table-filter"
                      disabled={loading}
                    >
                      {filterOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              }
            />
          </div>
        ) : (
          <div className="admin-table-empty">{t.empty}</div>
        )}
      </div>
    </div>
  );
};

export default DevicePlanLifecycle;
